/**
 * 定時連絡 API（/api/fixed-reports）。担当: G2
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { GearFindingDto, SightingDto } from '@/features/voyage-reports/sightings';

export interface WorkSummaryDto {
  id?: number;
  startTime: string | null;
  endTime: string | null;
  surveyArea: string | null;
  foundGear: string | null;
  foundVessel: string | null;
  other: string | null;
}
export interface WorkPlanDto {
  id?: number;
  surveyArea: string | null;
  other: string | null;
}

export interface FixedReportDto {
  id: number;
  fiscalYear: number;
  businessOperatorId: number;
  businessOperatorName: string;
  vesselId: number;
  vesselName: string;
  vesselNameKana?: string;
  registrationNo: string;
  noticeVesselId: number | null;
  periodFrom: string;
  periodTo: string;
  reportDate: string;
  reportTime: string | null;
  captainName: string | null;
  surveyor2Name: string | null;
  surveyor3Name: string | null;
  latDeg: string | null;
  latMin: string | null;
  latSec: string | null;
  lonDeg: string | null;
  lonMin: string | null;
  lonSec: string | null;
  zoneNo: string | null;
  course: string | null;
  speed: string | null;
  weather: string | null;
  pressure: string | null;
  visibility: string | null;
  windDirection: string | null;
  windForce: string | null;
  waves: string | null;
  swell: string | null;
  otherNotes: string | null;
  hasSighting: boolean;
  hasGearFinding: boolean;
  workSummaries: WorkSummaryDto[];
  workPlans: WorkPlanDto[];
  sightings?: SightingDto[];
  gearFindings?: GearFindingDto[];
  updatedAt?: string;
}

export interface CopyCandidate {
  noticeVesselId: number;
  vesselId: number;
  vesselName: string | null;
  registrationNo: string;
  departureDate: string;
  arrivalDate: string;
  existingId: number | null;
}

export interface FixedNewContext {
  existingId: number | null;
  prefill: {
    noticeVesselId: number;
    fiscalYear: number;
    businessOperatorId: number;
    businessOperatorName: string;
    vesselId: number;
    vesselName: string;
    vesselNameKana: string;
    registrationNo: string;
    periodFrom: string;
    periodTo: string;
    reportDate: string;
    reportTime: string;
    captainName: string;
    captainOptions: string[];
    crewOptions: string[];
  };
  copyCandidates: CopyCandidate[];
  sightings: SightingDto[];
  gearFindings: GearFindingDto[];
}

export interface FixedListFilter {
  businessOperatorId?: number | '';
  vesselName?: string;
  dateFrom?: string;
  dateTo?: string;
  hasSighting?: 'true' | '';
  hasGearFinding?: 'true' | '';
  page?: number;
}

export const fixedKeys = {
  all: ['fixed-reports'] as const,
  list: (year: number, f: FixedListFilter) => [...fixedKeys.all, 'list', year, f] as const,
  detail: (id: number) => [...fixedKeys.all, 'detail', id] as const,
  context: (rowId: number, date: string) => [...fixedKeys.all, 'context', rowId, date] as const,
};

export const PAGE_SIZE = 50;

export function useFixedReports(year: number, f: FixedListFilter) {
  return useQuery({
    queryKey: fixedKeys.list(year, f),
    queryFn: ({ signal }) => api.get<{ items: FixedReportDto[]; total: number }>('/fixed-reports', { fiscalYear: year, pageSize: PAGE_SIZE, ...f }, { signal }),
    placeholderData: keepPreviousData,
  });
}

export function useFixedReport(id: number | null) {
  return useQuery({
    queryKey: fixedKeys.detail(id ?? 0),
    queryFn: ({ signal }) => api.get<FixedReportDto>(`/fixed-reports/${id}`, undefined, { signal }),
    enabled: id != null,
  });
}

export function useFixedNewContext(rowId: number | null, date: string | null) {
  return useQuery({
    queryKey: fixedKeys.context(rowId ?? 0, date ?? ''),
    queryFn: ({ signal }) => api.get<FixedNewContext>('/fixed-reports/new-context', { noticeVesselId: rowId, date }, { signal }),
    enabled: rowId != null && !!date,
  });
}

export function useSaveFixedReport(id: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: unknown) =>
      id
        ? api.put<{ id: number; copied: { noticeVesselId: number; id: number | null; skipped: boolean }[] }>(`/fixed-reports/${id}`, input)
        : api.post<{ id: number; copied: { noticeVesselId: number; id: number | null; skipped: boolean }[] }>('/fixed-reports', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['advance-notices'] });
      // 新規作成用コンテキスト（existingId）は保存直後に再取得しない。再取得すると登録画面が
      // 「既存あり → 編集画面へ」リダイレクトし、保存後の詳細画面への遷移を上書きする（障害 BUG-004）。
      void qc.invalidateQueries({ queryKey: [...fixedKeys.all, 'context'], refetchType: 'none' });
      return qc.invalidateQueries({ queryKey: fixedKeys.all, predicate: (q) => q.queryKey[1] !== 'context' });
    },
  });
}

export function useDeleteFixedReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/fixed-reports/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: fixedKeys.all }),
  });
}

/** 緯度経度の表示 */
export const dms = (d?: string | null, m?: string | null, s?: string | null) => (d || m ? `${d ?? ''}°${m ?? ''}′${s ?? ''}` : '');
